/**
 * Périmètre Pilotage de l'utilisateur connecté.
 *
 * - `customer` : gestionnaire du client (administrator / agent / reporter),
 *   il voit tout le client.
 * - `networks` : administrateur d'au moins un Réseau (rôle client `member`
 *   donné par l'API à la promotion), il gère ses Réseaux.
 * - `communities` : « responsable de communauté », il n'administre que des
 *   communautés ou sous-communautés — Pilotage se limite à celles-ci.
 *
 * Les groupes administrés viennent de GET /me/groups/managed.
 */
export type ManagedGroup = {
  id: string;
  name: string;
  type: string;
  lvl: number;
  code: number | null;
  parent: { id: string; name: string } | null;
  root: { id: string; name: string } | null;
};

export type ScopeMode = 'customer' | 'networks' | 'communities';

export type Scope = {
  mode: ScopeMode;
  managedGroups: ManagedGroup[];
  /** false tant que /me/groups/managed n'a pas répondu */
  loaded: boolean;
};

const CUSTOMER_MANAGER_ROLES = ['administrator', 'agent', 'reporter'];

/**
 * Gestionnaire du compte client. Ces trois rôles sont les seuls à pouvoir
 * lister les gestionnaires (GET /customers/:id/members) : l'API répond 406 à
 * un administrateur de Réseau ou de communauté, dont le rôle client est
 * `member`. Tout ce qui dépend de cette liste doit passer par ce test.
 */
export const isCustomerManager = (customerRole?: string): boolean =>
  !!customerRole && CUSTOMER_MANAGER_ROLES.includes(customerRole);

export const getScopeMode = (
  customerRole?: string,
  managedGroups: ManagedGroup[] = [],
): ScopeMode => {
  if (isCustomerManager(customerRole)) {
    return 'customer';
  }
  if (managedGroups.some(group => group.lvl === 0)) {
    return 'networks';
  }
  return 'communities';
};

export const EMPTY_SCOPE: Scope = {
  mode: 'customer',
  managedGroups: [],
  loaded: false,
};

/** Libellé d'un groupe géré selon son niveau (0 = Réseau). */
/**
 * Réseaux où un responsable de communauté peut publier un évènement : ceux des
 * communautés et sous-communautés qu'il administre. L'évènement y est signé par une de
 * ses communautés.
 */
export const eventNetworks = (
  managedGroups: ManagedGroup[]
): Array<{ id: string; name: string }> =>
  managedGroups
    .filter(group => group.lvl > 0 && group.root)
    .map(group => group.root!)
    .filter(
      (network, index, networks) =>
        networks.findIndex(other => other.id === network.id) === index
    );

export const managedGroupLabel = (group: ManagedGroup): string =>
  group.lvl === 0
    ? 'Réseau'
    : group.lvl >= 2
      ? 'Sous-communauté'
      : 'Communauté';
